import { useTeamMembers } from '@/hooks/api/team';
import { useTranslation } from '@/hooks/use-translation';
import { $apiAllo } from '@/services/api-allo/client';
import { useMemo } from 'react';
import { ACTIVITY_TYPES } from './activity-filter-config';

export function useActivityFilterOptions() {
  const { tRaw: t } = useTranslation('desktop_ui');
  const { data: teamMembers, isLoading } = useTeamMembers();
  const { data: userData } = $apiAllo.useQuery('get', '/v1/user', {});
  const currentUserId = userData?.data?.id;

  const teamMemberOptions = useMemo(() => {
    if (!teamMembers) {
      return [];
    }
    const youLabel = t('components.companies.filter.activity.you').toLowerCase();
    return teamMembers
      .filter((member) => member.is_active)
      .map((member) => {
        const baseLabel = member.name ?? member.email ?? 'Unknown';
        const isCurrentUser = member.id === currentUserId;
        return {
          value: member.id ?? '',
          label: isCurrentUser ? `${baseLabel} (${youLabel})` : baseLabel,
          isCurrentUser,
        };
      })
      .filter((option) => option.value)
      .sort((a, b) => {
        // Current user ("You") always first
        if (a.isCurrentUser) {
          return -1;
        }
        if (b.isCurrentUser) {
          return 1;
        }
        return 0;
      });
  }, [teamMembers, currentUserId, t]);

  const activityTypeOptions = useMemo(() => {
    return ACTIVITY_TYPES.map((type) => ({
      value: type,
      label: t(`components.companies.filter.activity.types.${type}` as never),
    }));
  }, [t]);

  return {
    teamMemberOptions,
    activityTypeOptions,
    isLoading,
  };
}
